import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { BLAST_KINDS, useAdminSession, useAdminData, useAdminActions } from "../hooks/useAdminData";
import { sortLeads, perkRows } from "../lib/leadStats";
import { AdminLoginCard } from "../components/admin/AdminLoginCard";
import { NoAccessCard } from "../components/admin/NoAccessCard";
import { AdminHeader } from "../components/admin/AdminHeader";
import { AdminStats } from "../components/admin/AdminStats";
import { AdminToolbar } from "../components/admin/AdminToolbar";
import { ApplicationsTable } from "../components/admin/ApplicationsTable";
import { LeadsTable } from "../components/admin/LeadsTable";
import { PerksTable } from "../components/admin/PerksTable";
import { SourceChart } from "../components/admin/SourceChart";
import { BlastDialog } from "../components/admin/BlastDialog";

const AdminPanel = ({ tab, apps, leads, busy, onDecide, onHonour }) => {
  if (tab === "applications") return <ApplicationsTable rows={apps} busy={busy} onDecide={onDecide} />;
  if (tab === "leads") return <LeadsTable rows={sortLeads(leads)} />;
  return <PerksTable rows={perkRows(leads)} busy={busy} onHonour={onHonour} />;
};

export default function Admin() {
  const location = useLocation();
  const { user, setUser, logout } = useAdminSession(location.state?.user);
  const onUnauthorized = useCallback(() => setUser(false), [setUser]);
  const { leads, setLeads, apps, setApps, loadData } = useAdminData(user, onUnauthorized);
  const actions = useAdminActions({ loadData, setLeads, setApps });
  const [tab, setTab] = useState("applications");

  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-mist font-manrope text-sm font-semibold text-forest/50">
        Checking session…
      </div>
    );

  if (!user) return <AdminLoginCard onSuccess={setUser} />;

  if (user.role !== "admin") return <NoAccessCard email={user.email} onSignOut={logout} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-mist font-manrope">
      <AdminHeader onDigest={actions.sendDigest} onRefresh={() => loadData()} onLogout={logout} />

      <main className="mx-auto max-w-[1200px] px-6 py-10">
        <AdminStats leads={leads} apps={apps} />
        <AdminToolbar
          tab={tab}
          onTab={setTab}
          leads={leads}
          onOpenBlast={actions.openBlast}
          onExport={() => actions.exportCsv(tab)}
        />
        <div className="mt-6 overflow-x-auto rounded-3xl border border-forest/10 bg-white">
          <AdminPanel
            tab={tab}
            apps={apps}
            leads={leads}
            busy={actions.busy}
            onDecide={actions.decide}
            onHonour={actions.honourPerk}
          />
        </div>
        {tab === "leads" && <SourceChart leads={leads} />}
      </main>

      <BlastDialog
        open={actions.blastKind !== null}
        onOpenChange={(v) => !v && actions.setBlastKind(null)}
        title={actions.blastKind ? BLAST_KINDS[actions.blastKind].title : ""}
        preview={actions.blastPreview}
        sending={actions.blastSending}
        onConfirm={actions.confirmBlast}
      />
    </div>
  );
}
